import { useRef, useEffect, useState } from "react";
import { useInView } from "framer-motion";
import Reveal from "@/components/Reveal";
import { STATS } from "@/lib/content";

// Subtle count-up: animates once when scrolled into view, ~1.4s easeOut. No looping.
const CountUp = ({ value, duration = 1400 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  const match = String(value).match(/^([\d.]+)(.*)$/);
  const target = match ? parseFloat(match[1]) : 0;
  const suffix = match ? match[2] : "";

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  return (
    <span ref={ref}>
      {Math.round(display)}
      {suffix}
    </span>
  );
};

const StatsBand = () => {
  return (
    <section className="bg-[#2A2E7A]" data-testid="stats-band">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <Reveal className="max-w-2xl">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#A6B2E0]">
            By the numbers
          </p>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-white leading-[1.15]">
            A track record built on trust and consistency
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 lg:divide-x lg:divide-white/12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div data-testid={`stat-${i + 1}`} className="lg:px-8 lg:first:pl-0">
                <p className="font-heading text-[2.75rem] sm:text-[3.25rem] font-bold text-white leading-none tabular-nums tracking-[-0.02em]">
                  <CountUp value={s.value} />
                </p>
                <p className="mt-3.5 text-[15px] font-medium text-[#C0C8EA]">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsBand;
